import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import useTranslation from '@/hooks/useTranslation';
import { Button } from '../ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';
import { getUnreadCount } from '@/lib/api';
import OnboardingTour from '../Tour/OnboardingTour';
import TexHubLogo from '../brand/TexHubLogo';
import MobileBottomNav from './MobileBottomNav';
import RegionSelector from '../RegionSelector';
import {
  LayoutDashboard,
  Package,
  FileText,
  Ship,
  MessageSquare,
  ShoppingCart,
  ShoppingBag,
  Sparkles,
  CreditCard,
  Bell,
  Settings,
  LogOut,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Menu,
  Search,
  Building2,
  Users,
  BarChart3,
  Bookmark,
  User,
  Home,
  Zap,
  Crown,
  Factory,
  Megaphone,
  Wrench,
  FolderOpen,
  Star,
  Gift,
  Store,
  ExternalLink,
  Heart,
  X,
  HelpCircle,
  FileSpreadsheet
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Module-level variable to persist sidebar scroll position across re-renders
// This persists even when the component re-mounts
let savedSidebarScrollTop = 0;

const DashboardLayout = () => {
  const { user, logout, isAuthenticated, loading, unreadNotifications } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { language, isUSA } = useLocale();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [directoryOpen, setDirectoryOpen] = useState(false);
  const [sponsors, setSponsors] = useState([]);
  
  // Use real-time unread count from WebSocket
  const unreadCount = unreadNotifications;

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, loading, navigate]);

  // Check if seller needs to complete onboarding (has < 3 products)
  useEffect(() => {
    if (!loading && isAuthenticated && user) {
      // Only check for sellers (not buyers)
      const isSeller = user.account_type !== 'buyer';
      const needsOnboarding = user.onboarding_completed === false;
      const hasEnoughProducts = (user.products_count || 0) >= 3;
      
      // Redirect to onboarding if seller hasn't completed it
      if (isSeller && needsOnboarding && !hasEnoughProducts) {
        // Don't redirect if already on onboarding page
        if (!location.pathname.includes('seller-onboarding')) {
          navigate('/seller-onboarding');
        }
      }
    }
  }, [loading, isAuthenticated, user, navigate, location.pathname]);

  useEffect(() => {
    // Fetch sponsors - get up to 3 to display statically
    fetch(`${API_URL}/api/sponsors`)
      .then(res => res.ok ? res.json() : [])
      .then(data => setSponsors(data.slice(0, 3))) // Max 3 sponsors to display at once
      .catch(() => setSponsors([]));
  }, []);

  // Note: unreadCount now comes from WebSocket via AuthContext (real-time updates)

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Must define hooks before any conditional returns
  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const isAdmin = user?.role === 'admin';

  const isPro = user?.subscription_plan === 'pro';

  // Check if user is a buyer (limited access)
  const isBuyer = user?.account_type === 'buyer';

  // Main sidebar items - Different for buyers vs sellers
  const mainNavItems = isBuyer ? [
    // Buyer menu - limited options
    { path: '/dashboard', icon: <LayoutDashboard className="w-5 h-5" />, label: isUSA ? 'Dashboard' : 'Inicio' },
    { path: '/tiendas', icon: <Store className="w-5 h-5" />, label: isUSA ? 'Explore Stores' : 'Explorar Tiendas' },
    { path: '/dashboard/store-orders', icon: <ShoppingBag className="w-5 h-5" />, label: isUSA ? 'My Orders' : 'Mis Pedidos' },
    { path: '/dashboard/profile', icon: <User className="w-5 h-5" />, label: isUSA ? 'My Profile' : 'Mi Perfil' },
  ] : [
    // Seller menu - full access
    { path: '/dashboard', icon: <LayoutDashboard className="w-5 h-5" />, label: t('nav', 'dashboard') },
    { path: `/company/${user?.company_id}`, icon: <User className="w-5 h-5" />, label: t('nav', 'profile'), show: !!user?.company_id },
    { path: '/dashboard/saved', icon: <Heart className="w-5 h-5" />, label: isUSA ? 'Favorites' : 'Favoritos' },
    { path: '/dashboard/products', icon: <FolderOpen className="w-5 h-5" />, label: t('nav', 'catalog') },
    { path: '/dashboard/store', icon: <Store className="w-5 h-5" />, label: isUSA ? 'My Store' : 'Mi Tienda' },
    { path: '/dashboard/texvision', icon: <Sparkles className="w-5 h-5" />, label: 'TexHub AI', premium: true, highlight: true },
    { path: '/dashboard/analytics', icon: <BarChart3 className="w-5 h-5" />, label: 'Analytics', premium: true },
    { path: '/surplus', icon: <Zap className="w-5 h-5" />, label: t('nav', 'surplus') },
    { path: '/dashboard/blog', icon: <Megaphone className="w-5 h-5" />, label: t('nav', 'blog') },
    { path: '/dashboard/quotes', icon: <FileText className="w-5 h-5" />, label: isUSA ? 'Quotes' : 'Cotizaciones' },
    { path: '/dashboard/messages', icon: <MessageSquare className="w-5 h-5" />, label: t('nav', 'messages') },
    { path: '/dashboard/orders', icon: <ShoppingCart className="w-5 h-5" />, label: isUSA ? 'Orders' : 'Pedidos' },
    { path: '/dashboard/store-orders', icon: <ShoppingBag className="w-5 h-5" />, label: isUSA ? 'Store Orders' : 'Pedidos de Tiendas' },
    { path: '/dashboard/team', icon: <Users className="w-5 h-5" />, label: isUSA ? 'My Team' : 'Mi Equipo' },
  ].filter(item => item.show !== false);

  // Directory items for header dropdown
  const directoryItems = [
    { path: '/search?type=providers', icon: <Building2 className="w-5 h-5" />, label: t('nav', 'providers') },
    { path: '/search?type=services', icon: <Wrench className="w-5 h-5" />, label: t('nav', 'services') },
    { path: '/search?type=fabricantes', icon: <Users className="w-5 h-5" />, label: t('nav', 'manufacturers') },
    { path: '/search?type=tiendas', icon: <Store className="w-5 h-5" />, label: isUSA ? 'Stores' : 'Tiendas' },
  ];

  const adminNavItems = [
    { path: '/dashboard/admin', icon: <BarChart3 className="w-5 h-5" />, label: isUSA ? 'Admin Panel' : 'Panel Admin' },
  ];

  const NavLink = ({ item, onClick }) => {
    // Handle paths with query strings
    const itemPath = item.path.split('?')[0];
    const itemQuery = item.path.includes('?') ? item.path.split('?')[1] : null;
    const currentPath = location.pathname;
    const currentQuery = location.search.slice(1); // Remove leading '?'
    
    // Check if active - match path and optionally query string
    const isActive = itemQuery 
      ? (currentPath === itemPath && currentQuery.includes(itemQuery.split('=')[1]))
      : (currentPath === item.path || currentPath === itemPath);
    
    const showPremiumLock = item.premium && !isPro;
    
    // Special highlight style for TexVision AI - Amber/gold for more visibility
    const highlightClass = item.highlight 
      ? 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 text-amber-700 hover:from-amber-100 hover:to-orange-100 font-medium' 
      : '';
    
    return (
      <Link
        to={item.path}
        onClick={onClick}
        className={`sidebar-link ${isActive ? 'sidebar-link-active' : ''} ${showPremiumLock ? 'opacity-60' : ''} ${highlightClass}`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
      >
        <span className={item.highlight ? 'text-amber-600' : ''}>{item.icon}</span>
        <span>{item.label}</span>
        {showPremiumLock && <Crown className="w-4 h-4 ml-auto text-yellow-500" />}
      </Link>
    );
  };

  // Memoized SidebarContent to prevent scroll reset on parent re-renders
  const SidebarContent = React.memo(({ onLinkClick }) => {
    const navRef = useRef(null);
    
    // Restore scroll position on mount and save on scroll
    useEffect(() => {
      const nav = navRef.current;
      if (nav) {
        // Restore saved scroll position from module-level variable
        nav.scrollTop = savedSidebarScrollTop;
        
        // Save scroll position on scroll to module-level variable
        const handleScroll = () => {
          savedSidebarScrollTop = nav.scrollTop;
        };
        
        nav.addEventListener('scroll', handleScroll);
        return () => {
          if (nav) savedSidebarScrollTop = nav.scrollTop;
          nav.removeEventListener('scroll', handleScroll);
        };
      }
    }, []);
    
    return (
      <div className="flex flex-col h-full overflow-hidden">
        <div className="p-4 flex-shrink-0">
          <Link to="/search">
            <Button variant="outline" className="w-full justify-start gap-2" data-testid="search-btn">
              <Search className="w-4 h-4" />
              {t('common', 'search')}
            </Button>
          </Link>
        </div>

        {/* Scrollable navigation area */}
        <nav 
          ref={navRef}
          className="flex-1 px-3 overflow-y-auto overflow-x-hidden"
          style={{ 
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin'
          }}
        >
          <div className="space-y-1 pb-4">
            {mainNavItems.map((item) => (
              <NavLink key={item.path} item={item} onClick={onLinkClick} />
            ))}
          </div>

          {isAdmin && (
            <>
              <div className="my-4 px-4">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  {isUSA ? 'Administration' : 'Administración'}
                </div>
              </div>
              <div className="space-y-1 pb-4">
                {adminNavItems.map((item) => (
                  <NavLink key={item.path} item={item} onClick={onLinkClick} />
                ))}
              </div>
            </>
          )}
          
          {/* Plans button inside scroll for mobile */}
          <div className="lg:hidden p-4 border-t border-slate-200 mt-4">
            <Link to="/dashboard/subscription" onClick={onLinkClick}>
              <Button variant="outline" className="w-full justify-start gap-2" data-testid="subscription-btn-mobile">
                <CreditCard className="w-4 h-4" />
                {user?.subscription_plan === 'free' || !user?.subscription_plan
                  ? (isUSA ? 'Upgrade plan' : 'Mejorar plan') 
                  : (isUSA ? 'Plans & billing' : 'Planes y suscripciones')}
              </Button>
            </Link>
          </div>
          
          {/* Extra padding for mobile footer */}
          <div className="lg:hidden h-32" />
        </nav>

        {/* Plans button - Fixed on desktop, inside scroll on mobile */}
        <div className="hidden lg:block p-4 border-t border-slate-200 flex-shrink-0">
          <Link to="/dashboard/subscription" onClick={onLinkClick}>
            <Button variant="outline" className="w-full justify-start gap-2" data-testid="subscription-btn">
              <CreditCard className="w-4 h-4" />
              {user?.subscription_plan === 'free' || !user?.subscription_plan
                ? (isUSA ? 'Upgrade plan' : 'Mejorar plan') 
                : (isUSA ? 'Plans & billing' : 'Planes y suscripciones')}
            </Button>
          </Link>
        </div>
      </div>
    );
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-white border-r border-slate-200">
        <SidebarContent />
      </aside>

      {/* Mobile Header - Always visible and fixed */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between h-14 px-3">
          {/* Left side: Menu + Logo */}
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon" 
              className="shrink-0 h-9 w-9"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu className="w-5 h-5" />
            </Button>
            <Link to="/dashboard" className="flex items-center">
              <TexHubLogo size="navbar" />
            </Link>
          </div>

          {/* Center: Navigation buttons */}
          <div className="flex items-center gap-1">
            {/* Directory Dropdown */}
            <div className="relative">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-xs px-2 h-8"
                onClick={() => setDirectoryOpen(!directoryOpen)}
              >
                <Building2 className="w-4 h-4 mr-1" />
                {isUSA ? 'Directory' : 'Directorio'}
                <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${directoryOpen ? 'rotate-180' : ''}`} />
              </Button>
              
              {directoryOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setDirectoryOpen(false)}
                  />
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border z-50">
                    {directoryItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setDirectoryOpen(false)}
                        className="flex items-center gap-2 px-4 py-3 hover:bg-slate-50 text-sm"
                      >
                        {item.icon}
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {(user?.subscription_plan === 'free' || !user?.subscription_plan) && (
              <Link to="/dashboard/subscription">
                <Button 
                  size="sm"
                  className="text-xs px-2 h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Crown className="w-3 h-3 mr-1" />
                  Pro
                </Button>
              </Link>
            )}
          </div>

          {/* Right side: Region Selector + User Menu */}
          <div className="flex items-center gap-1">
            {/* Region/Language Selector */}
            <RegionSelector variant="minimal" />
            
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 relative" data-testid="mobile-user-menu-btn">
                  <img 
                    src={user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=0f172a&color=fff`}
                    alt={user?.name}
                    className="w-7 h-7 rounded-full"
                  />
                  {unreadCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center p-0 text-[10px]" style={{ backgroundColor: '#34B772' }}>
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard/notifications')}>
                  <Bell className="mr-2 h-4 w-4" />
                  {isUSA ? 'Notifications' : 'Notificaciones'}
                  {unreadCount > 0 && (
                    <Badge className="ml-auto h-5 px-1.5 text-[10px]" style={{ backgroundColor: '#34B772' }}>
                      {unreadCount}
                    </Badge>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/company')}>
                  <Building2 className="mr-2 h-4 w-4" />
                  {isUSA ? 'My company' : 'Mi empresa'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  {isUSA ? 'Settings' : 'Configuración'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/subscription')}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  {isUSA ? 'Plans & Billing' : 'Planes y Suscripciones'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('common', 'logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Spacer for fixed mobile header */}
      <div className="lg:hidden h-14" />

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-50 bg-black/50"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div 
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-white transform transition-transform duration-300 ease-in-out flex flex-col ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <TexHubLogo size="navbar" />
          <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)}>
            <X className="w-5 h-5" />
          </Button>
        </div>
        {/* Single scroll container for mobile sidebar */}
        <div className="flex-1 overflow-hidden min-h-0">
          <SidebarContent onLinkClick={closeMobileMenu} />
        </div>
      </div>

      {/* Main Content Area */}
      <div className="lg:pl-64">
        {/* Desktop Top Bar */}
        <header className="hidden lg:flex items-center justify-between h-16 px-8 bg-white border-b border-slate-200">
          {/* Left side - Logo */}
          <div className="flex items-center">
            <Link to="/dashboard">
              <TexHubLogo size="large" />
            </Link>
          </div>
          
          {/* Right side - Actions */}
          <div className="flex items-center gap-4">
            {/* Directory Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="desktop-directory-dropdown">
                  <FolderOpen className="w-5 h-5 text-emerald-600" />
                  <span className="font-medium">{isUSA ? 'Directory' : 'Directorio'}</span>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {directoryItems.map((item) => (
                  <DropdownMenuItem key={item.path} onClick={() => navigate(item.path)}>
                    {item.icon}
                    <span className="ml-2">{item.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Store Link */}
            <Link to="/dashboard/store" className="hidden md:flex items-center gap-2 text-slate-600 hover:text-emerald-600 transition-colors">
              <Store className="w-5 h-5" />
              <span className="text-sm font-medium">{t('nav', 'myStore')}</span>
            </Link>
            
            {/* Subscription Status & Upgrade Button */}
            <Link to="/dashboard/subscription" className="flex items-center gap-2">
              {user?.subscription_plan === 'pro' ? (
                <Badge className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-3 py-1">
                  <Crown className="w-3 h-3 mr-1" />
                  PRO
                </Badge>
              ) : (
                <Button 
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white shadow-md"
                  data-testid="upgrade-premium-btn"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  {isUSA ? 'Upgrade Plan' : 'Mejorar Plan'}
                </Button>
              )}
            </Link>
            
            <Link to="/dashboard/notifications">
              <Button variant="ghost" size="icon" className="relative" data-testid="desktop-notifications-btn">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-emerald-600">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </Badge>
                )}
              </Button>
            </Link>

            {/* Region Selector - Desktop */}
            <RegionSelector variant="minimal" showLabel={true} />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <img 
                    src={user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=0f172a&color=fff`}
                    alt={user?.name}
                    className="w-8 h-8 rounded-full"
                  />
                  <span className="font-medium">{user?.name}</span>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                  <div className="mt-2">
                    {user?.subscription_plan === 'pro' ? (
                      <Badge className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-xs">
                        <Crown className="w-3 h-3 mr-1" /> Plan PRO
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs text-slate-500">
                        {isUSA ? 'Free Plan' : 'Plan Gratis'}
                      </Badge>
                    )}
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard/subscription')}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  {isUSA ? 'Plans & Billing' : 'Planes y Suscripciones'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/company')}>
                  <Building2 className="mr-2 h-4 w-4" />
                  {isUSA ? 'My company' : 'Mi empresa'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  {isUSA ? 'Settings' : 'Configuración'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/help/excel')}>
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  {isUSA ? 'Excel Import Guide' : 'Guía de Importación Excel'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => window.open('mailto:soporte@texhub.com.mx', '_blank')}>
                  <HelpCircle className="mr-2 h-4 w-4" />
                  {isUSA ? 'Help & Support' : 'Ayuda y Soporte'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('common', 'logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Sponsors Banner - Static 3 sponsors (Desktop) */}
        {sponsors.length > 0 && (
          <div className="hidden lg:flex items-center justify-between px-8 py-3 bg-gradient-to-r from-amber-100 via-yellow-100 to-amber-100 border-b border-amber-200 shadow-sm">
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-600" />
              <span className="text-sm font-bold text-amber-700 uppercase tracking-wider">
                {isUSA ? 'Sponsored by:' : 'Patrocinado por:'}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {sponsors.map((sponsor) => (
                <Link 
                  key={sponsor.company_id || sponsor._id}
                  to={`/company/${sponsor.company_id}`}
                  className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border-2 border-amber-300 hover:border-amber-400 hover:shadow-lg transition-all group"
                >
                  <div className="w-7 h-7 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center flex-shrink-0">
                    {(sponsor.logo || sponsor.logo_url) ? (
                      <img 
                        src={sponsor.logo || sponsor.logo_url} 
                        alt={sponsor.company_name} 
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Building2 className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                  <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900 truncate max-w-[120px]">
                    {sponsor.company_name}
                  </span>
                </Link>
              ))}
            </div>
            <Link 
              to="/dashboard/sponsors" 
              className="text-xs text-amber-700 hover:text-amber-800 font-bold px-3 py-1.5 bg-white rounded-full border border-amber-200 hover:border-amber-300 transition-colors"
            >
              {isUSA ? 'View all' : 'Ver todos'}
            </Link>
          </div>
        )}

        {/* Sponsors Banner - Mobile (Compact) */}
        {sponsors.length > 0 && (
          <div className="lg:hidden flex items-center justify-between px-3 py-2 bg-gradient-to-r from-amber-100 via-yellow-100 to-amber-100 border-b border-amber-200">
            <div className="flex items-center gap-1">
              <Crown className="w-4 h-4 text-amber-600" />
              <span className="text-xs font-bold text-amber-700">
                {isUSA ? 'Sponsors:' : 'Patrocinado:'}
              </span>
            </div>
            <div className="flex items-center gap-2 overflow-x-auto">
              {sponsors.map((sponsor) => (
                <Link 
                  key={sponsor.company_id || sponsor._id}
                  to={`/company/${sponsor.company_id}`}
                  className="flex items-center gap-1 px-2 py-1 bg-white rounded-full border border-amber-300 text-xs whitespace-nowrap"
                >
                  <div className="w-5 h-5 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center flex-shrink-0">
                    {(sponsor.logo || sponsor.logo_url) ? (
                      <img 
                        src={sponsor.logo || sponsor.logo_url} 
                        alt={sponsor.company_name} 
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Building2 className="w-3 h-3 text-slate-400" />
                    )}
                  </div>
                  <span className="font-medium text-slate-700 truncate max-w-[80px]">
                    {sponsor.company_name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Page Content */}
        <main className="p-4 lg:p-8 pb-24 lg:pb-8">
          <Outlet />
        </main>
      </div>

      {/* Bottom Navigation Bar - Mobile Only */}
      <MobileBottomNav unreadCount={unreadCount} />

      {/* Onboarding Tour */}
      <OnboardingTour user={user} />
    </div>
  );
};

export default DashboardLayout;
